import { ChangeDetectionStrategy, Component, OnDestroy, Renderer2, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Observable, Subscription, filter, map } from 'rxjs';
import { MenuService } from './menu/services/app.menu.service';
import { AppConfig, LayoutService } from './service/app.layout.service';
import { AppTopbarComponent } from './topbar/app.topbar.component';
import { NgClass, AsyncPipe } from '@angular/common';
import { AppRightMenuComponent } from './rightmenu/app.rightmenu.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Default,
  selector: 'app-layout',
  templateUrl: './app.layout.component.html',
  imports: [NgClass, AppTopbarComponent, RouterOutlet, AppRightMenuComponent, AsyncPipe],
})
export class AppLayoutComponent implements OnDestroy {
  private readonly menuService = inject(MenuService);
  readonly layoutService = inject(LayoutService);
  readonly renderer = inject(Renderer2);
  readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  public adjustHeight = false;
  public overlayMenuOpenSubscription: Subscription;

  public menuOutsideClickListener: any;

  @ViewChild(AppTopbarComponent) public appTopbar!: AppTopbarComponent;

  public config$ = this.layoutService.config$;

  constructor() {
    this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
      if (!this.menuOutsideClickListener) {
        this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
          const isOutsideClicked = !(
            this.appTopbar.appSidebar.el.nativeElement.isSameNode(event.target) ||
            this.appTopbar.appSidebar.el.nativeElement.contains(event.target) ||
            this.appTopbar.menuButton.nativeElement.isSameNode(event.target) ||
            this.appTopbar.menuButton.nativeElement.contains(event.target)
          );
          if (isOutsideClicked) {
            this.hideMenu();
          }
        });
      }

      if (this.layoutService.state.staticMenuMobileActive) {
        this.blockBodyScroll();
      }
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.hideMenu();
      this.unblockBodyScroll();
      this.shouldHeightBeAdjustedByRouteURL();
    });
  }

  private shouldHeightBeAdjustedByRouteURL() {
    if (
      this.router.url.includes('inspectionItem') ||
      this.router.url.includes('pricebookItem') ||
      this.router.url.includes('strategy') ||
      this.router.url.includes('objectCategory') ||
      this.router.url.includes('layerFeatures') ||
      this.router.url.includes('documentcode') ||
      this.router.url.includes('inspection-list-list') ||
      this.router.url.includes('inspection-method-list') ||
      this.router.url.includes('elementgroup') ||
      this.router.url.includes('element') ||
      this.router.url.includes('objects/object')
    ) {
      this.adjustHeight = true;
    } else {
      this.adjustHeight = false;
    }
  }

  public blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  }

  public unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replace(
        new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'),
        ' ',
      );
    }
  }

  public hideMenu() {
    this.layoutService.state.overlayMenuActive = false;
    this.layoutService.state.staticMenuMobileActive = false;
    this.layoutService.state.menuHoverActive = false;
    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }
    this.unblockBodyScroll();
  }

  public containerClass(config: AppConfig) {
    return {
      'layout-overlay': config.menuMode === 'overlay',
      'layout-static': config.menuMode === 'static',
      'layout-slim': config.menuMode === 'slim',
      'layout-horizontal': config.menuMode === 'horizontal',
      'layout-compact': config.menuMode === 'compact',
      'layout-sidebar-dim': config.colorScheme === 'dim',
      'layout-sidebar-dark': config.colorScheme === 'dark',
      'layout-overlay-active':
        this.layoutService.state.overlayMenuActive || this.layoutService.state.staticMenuMobileActive,
      'layout-mobile-active': this.layoutService.state.staticMenuMobileActive,
      'layout-static-inactive': this.layoutService.state.staticMenuDesktopInactive && config.menuMode === 'static',
      'p-ripple-disabled': !config.ripple,
      'p-input-filled': config.inputStyle === 'filled',
    };
  }

  public sidebarClass(config: AppConfig) {
    return config.colorScheme === 'light' ? `layout-sidebar-${config.menuTheme}` : '';
  }

  public ngOnDestroy() {
    if (this.overlayMenuOpenSubscription) {
      this.overlayMenuOpenSubscription.unsubscribe();
    }

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
    }
  }

  public get isExpanded$(): Observable<boolean> {
    return this.route.url.pipe(
      map(() => {
        const regex = /scenario\/[0-9a-f]{10,}/;
        const match = this.router.url.match(regex);
        return match && !this.router.url.includes('chat') ? match?.length > 0 : false;
      }),
    );
  }
}
